import React from "react";
import { Node } from "@tiptap/core";
import {
  ReactNodeViewRenderer,
  NodeViewWrapper,
  NodeViewContent,
} from "@tiptap/react";

/* =========================================================
   ACCORDION NODEVIEW
========================================================= */
function AccordionNodeView(props) {
  const { editor, getPos, node } = props;

  const deleteAccordion = () => {
    const pos = getPos();

    editor
      .chain()
      .focus()
      .deleteRange({
        from: pos,
        to: pos + node.nodeSize,
      })
      .run();
  };

  const addItem = () => {
    const pos = getPos();

    editor
      .chain()
      .focus()
      .insertContentAt(pos + node.nodeSize - 1, {
        type: "accordionItem",
        content: [
          {
            type: "accordionTitle",
            content: [{ type: "paragraph", content: [{ type: "text", text: "Accordion Title" }] }],
          },
          {
            type: "accordionContent",
            content: [
              {
                type: "paragraph",
                content: [{ type: "text", text: "Accordion Description" }],
              },
            ],
          },
        ],
      })
      .run();
  };

  return (
    <NodeViewWrapper>
      <div className="rounded p-2 position-relative">

        {/* BACKGROUND */}
        <div className="position-absolute top-0 start-0 w-100 h-100 rounded bg-success opacity-25 accordion-div-editor" />

        {/* HEADER */}
        <div
          contentEditable={false}
          className="user-select-none d-flex justify-content-between mb-2 position-relative accordion-header-editor"
        >
          <div className="fw-bold text-dark">Accordion</div>

          <div className="d-flex align-items-center gap-2">
            <span
              className="material-icons pointer"
              onClick={deleteAccordion}
              title="Delete Accordion"
            >
              delete
            </span>
          </div>
        </div>

        <div className="position-relative">
          <NodeViewContent />
        </div>
      </div>
    </NodeViewWrapper>
  );
}

/* =========================================================
   ACCORDION ITEM NODEVIEW
========================================================= */
function AccordionItemNodeView(props) {
  const { editor, getPos, node } = props;

  const deleteItem = () => {
    const pos = getPos();

    editor
      .chain()
      .focus()
      .deleteRange({
        from: pos,
        to: pos + node.nodeSize,
      })
      .run();
  };

  const addAfter = () => {
    const pos = getPos();

    editor
      .chain()
      .focus()
      .insertContentAt(pos + node.nodeSize, {
        type: "accordionItem",
        content: [
          {
            type: "accordionTitle",
            content: [{ type: "paragraph", content: [{ type: "text", text: "Accordion Title" }] }],
          },
          {
            type: "accordionContent",
            content: [
              {
                type: "paragraph",
                content: [{ type: "text", text: "Accordion Description" }],
              },
            ],
          },
        ],
      })
      .run();
  };

  return (
    <NodeViewWrapper>
      <div className="rounded p-2 mb-2 position-relative text-white">

        {/* BACKGROUND */}
        <div
          className="position-absolute top-0 start-0 w-100 h-100 rounded"
          style={{ background: "#1a5356" }}
        />

        {/* HEADER */}
        <div
          contentEditable={false}
          className="user-select-none d-flex justify-content-between mb-2 position-relative accordion-title-editor"
        >
          <div>Accordion Item</div>

          <div className="d-flex align-items-center gap-2">
            <span
              className="material-icons pointer"
              onClick={addAfter}
              title="Add Item"
            >
              add
            </span>

            <span
              className="material-icons pointer"
              onClick={deleteItem}
              title="Delete Item"
            >
              delete
            </span>
          </div>
        </div>

        <NodeViewContent />
      </div>
    </NodeViewWrapper>
  );
}

/* =========================================================
   TITLE NODEVIEW
========================================================= */
function AccordionTitleNodeView() {
  return (
    <NodeViewWrapper>
      <div className="rounded p-2 mb-2 position-relative text-dark" style={{ color: "#000" }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white rounded accordion-text-editor" />

        <div className="position-relative">
          <NodeViewContent />
        </div>
      </div>
    </NodeViewWrapper>
  );
}

/* =========================================================
   CONTENT NODEVIEW
========================================================= */
function AccordionContentNodeView() {
  return (
    <NodeViewWrapper>
      <div className="rounded p-2 position-relative text-dark" style={{ color: "#000" }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white rounded accordion-text-editor" />

        <div className="position-relative">
          <NodeViewContent />
        </div>
      </div>
    </NodeViewWrapper>
  );
}

/* =========================================================
   ACCORDION EXTENSION
========================================================= */
export const Accordion = Node.create({
  name: "accordion",
  group: "block",
  content: "accordionItem+",
  selectable: false,

  parseHTML() {
    return [{ tag: "div[data-accordion]" }];
  },

  renderHTML() {
    return ["div", { "data-accordion": "", class: "accordion" }, 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AccordionNodeView);
  },
});

/* =========================================================
   ACCORDION ITEM EXTENSION
========================================================= */
export const AccordionItem = Node.create({
  name: "accordionItem",
  group: "block",
  content: "accordionTitle accordionContent",
  selectable: true,

  parseHTML() {
    return [{ tag: "div[data-accordion-item]" }];
  },

  renderHTML() {
    return ["div", { "data-accordion-item": "", class: "accordion-item" }, 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AccordionItemNodeView);
  },
});

/* =========================================================
   TITLE EXTENSION
========================================================= */
export const AccordionTitle = Node.create({
  name: "accordionTitle",
  group: "block",
  content: "block+",
  defining: true,

  parseHTML() {
    return [
      { tag: "div[data-accordion-title]" },
      { tag: "h2[data-accordion-title]" }
    ];
  },

  renderHTML() {
    return ["div", { "data-accordion-title": "" }, 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AccordionTitleNodeView);
  },
});

/* =========================================================
   CONTENT EXTENSION
========================================================= */
export const AccordionContent = Node.create({
  name: "accordionContent",
  group: "block",
  content: "block+",
  defining: true,

  parseHTML() {
    return [{ tag: "div[data-accordion-content]" }];
  },

  renderHTML() {
    return ["div", { "data-accordion-content": "" }, 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AccordionContentNodeView);
  },
});